import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { ArrowLeft, MapPin, Search, Loader2 } from "lucide-react";

export default function DistributorLocator() {
  const navigate = useNavigate();
  const [distributors, setDistributors] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [radius, setRadius] = useState(12);
  const [userLoc, setUserLoc] = useState(null);

  useEffect(() => {
    setLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(pos => {
        setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        loadDistributors(pos.coords.latitude, pos.coords.longitude, 12, "");
      }, () => { loadDistributors(null, null, 12, ""); });
    } else { loadDistributors(null, null, 12, ""); }
  }, []);

  const loadDistributors = async (lat, lng, r, q) => {
    setLoading(true);
    try {
      let url = `/distributors/search?limit=50&radius_km=${r}`;
      if (lat && lng) url += `&lat=${lat}&lng=${lng}`;
      if (q) url += `&q=${encodeURIComponent(q)}`;
      const { data } = await API.get(url);
      setDistributors(data || []);
    } catch {} finally { setLoading(false); }
  };

  const handleRadiusChange = (newRadius) => {
    setRadius(newRadius);
    if (userLoc) loadDistributors(userLoc.lat, userLoc.lng, newRadius, search);
    else loadDistributors(null, null, newRadius, search);
  };

  const handleSearch = (q) => {
    setSearch(q);
    if (q.length >= 2 || q === "") {
      if (userLoc) loadDistributors(userLoc.lat, userLoc.lng, radius, q);
      else loadDistributors(null, null, radius, q);
    }
  };

  return (
    <div className="space-y-4" data-testid="distributor-locator">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-gray-100"><ArrowLeft className="w-5 h-5" /></button>
        <div><h1 className="text-xl font-bold text-gray-900">Find Distributors</h1><p className="text-xs text-gray-500">563 distributors across India</p></div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
        <input type="text" value={search} onChange={e => handleSearch(e.target.value)}
          placeholder="Search by name, city, or pin code..."
          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#008A99]" data-testid="distributor-search-input" />
      </div>

      {/* Range Slider */}
      <div className="bg-white rounded-xl border p-4" data-testid="range-slider">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-gray-500">Search Radius</span>
          <span className="text-sm font-bold text-[#008A99]">{radius} km</span>
        </div>
        <input type="range" min="5" max="100" value={radius} onChange={e => handleRadiusChange(parseInt(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-[#008A99]" data-testid="radius-slider" />
        <div className="flex justify-between text-[10px] text-gray-400 mt-1">
          <span>5 km</span><span>25 km</span><span>50 km</span><span>100 km</span>
        </div>
      </div>

      {loading ? <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#008A99] mx-auto" /></div> : (
        <div className="space-y-2">
          <p className="text-xs text-gray-400">{distributors.length} distributors found</p>
          {distributors.map((d, i) => (
            <div key={i} className="bg-white rounded-xl p-4 border hover:border-[#008A99] transition-colors" data-testid="distributor-card">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-[#E0F4F5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#008A99]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm text-gray-900">{d.name}</h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] px-1.5 py-0.5 bg-[#E0F4F5] text-[#008A99] rounded font-medium">{d.db_type}</span>
                    <span className="text-[10px] text-gray-400">Code: {d.db_code}</span>
                    {d.distance_km != null && <span className="text-[10px] font-bold text-[#008A99]">{d.distance_km} km</span>}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{d.address}</p>
                  {d.pin_code && <p className="text-xs text-gray-400 mt-0.5">PIN: {d.pin_code}</p>}
                  {d.latitude > 0 && (
                    <a href={`https://maps.google.com/?q=${d.latitude},${d.longitude}`} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-[#008A99] font-medium mt-1 inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> View on Map
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
          {distributors.length === 0 && !loading && (
            <p className="text-center text-gray-400 py-8 text-sm">No distributors found within {radius}km. Try increasing the range.</p>
          )}
        </div>
      )}
    </div>
  );
}
